import { Flex, Heading, Text, VStack, HStack, Icon, Badge, Box, MenuRoot, MenuTrigger, MenuContent, MenuItem, type FlexProps } from '@chakra-ui/react';
import Button from './Button';
import { Plus, ChevronDown, RefreshCw } from 'lucide-react';
import { forwardRef } from 'react';
import type { BulkAction } from '@/components/section/EntityTableSection';

// Header styles using design tokens
const headerStyles = {
  justify: "space-between",
  align: "center", 
  mb: 0,
};

const titleStyles = {
  size: "lg" as const,
  color: "gray.800",
  _dark: { color: "gray.100" },
};

const subtitleStyles = {
  color: "gray.500", 
  _dark: { color: "gray.400" },
  fontSize: "sm",
};

export interface PageHeaderProps extends FlexProps {
  title: string;
  subtitle?: string;
  onAddNew?: () => void;
  addButtonText?: string;
  addButtonIcon?: React.ComponentType;
  actions?: React.ReactNode;
  bulkActions?: BulkAction[];
  selectedCount?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  function PageHeader({
    title,
    subtitle,
    onAddNew,
    addButtonText = "Create New",
    addButtonIcon = Plus,
    actions,
    bulkActions,
    selectedCount = 0,
    onRefresh,
    isRefreshing = false,
    ...props
  }, ref) {
    const hasBulkSelection = bulkActions && bulkActions.length > 0 && selectedCount > 0;

    return (
      <Flex ref={ref} {...headerStyles} {...props}>
        <VStack align="start" gap={1}>
          <Heading {...titleStyles}>
            {title}
          </Heading>
          <HStack gap={2}>
            {subtitle && (
              <Text {...subtitleStyles}>
                {subtitle}
              </Text>
            )}
            {hasBulkSelection && (
              <Badge colorPalette="brand" size="sm" variant="subtle" px={2} py={1} fontSize="xs">
                {selectedCount} selected
              </Badge>
            )}
          </HStack>
        </VStack>
        
        {(onAddNew || actions || hasBulkSelection || onRefresh) && (
          <HStack gap={3}>
            {hasBulkSelection && (
              <>
                {bulkActions.map((action) => (
                  <Box key={action.key} position="relative">
                    <MenuRoot positioning={{ placement: "bottom-end" }}>
                      <MenuTrigger asChild>
                        <Button
                          variant="outline"
                          colorScheme="gray"
                          disabled={action.disabled}
                        >
                          <Icon as={action.icon} boxSize={4} />
                          {action.label}
                          <Icon as={ChevronDown} boxSize={3.5} />
                        </Button>
                      </MenuTrigger>
                      <MenuContent
                        position="absolute"
                        top="100%"
                        right={0}
                        zIndex={1400}
                        bg="white"
                        borderColor="gray.200"
                        borderWidth={1}
                        borderRadius="md"
                        boxShadow="lg"
                        w="100%"
                        _dark={{ bg: "gray.700", borderColor: "gray.600" }}
                      >
                        {action.items.map((item) => (
                          <MenuItem
                            key={item.key}
                            value={item.key}
                            onClick={item.onClick}
                            color={item.colorPalette === "red" ? "red.500" : undefined}
                            px={3}
                            py={2}
                            _hover={{
                              bg: item.colorPalette === "red" ? "red.50" : "gray.100",
                              _dark: { bg: item.colorPalette === "red" ? "red.900" : "gray.700" },
                            }}
                          >
                            <Text fontSize="sm">{item.label}</Text>
                          </MenuItem>
                        ))}
                      </MenuContent>
                    </MenuRoot>
                  </Box>
                ))}
              </>
            )}
            {onRefresh && (
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={onRefresh}
                loading={isRefreshing}
                disabled={isRefreshing}
                title="Refresh data"
                aria-label="Refresh data"
              >
                <Icon as={RefreshCw} boxSize={4} />
              </Button>
            )}
            {onAddNew && (
              <Button colorPalette="brand" onClick={onAddNew}>
                <Icon as={addButtonIcon} boxSize={4} />
                {addButtonText}
              </Button>
            )}
            {actions}
          </HStack>
        )}
      </Flex>
    );
  }
);

export default PageHeader;
